import { Icon20Cancel } from '@vkontakte/icons';
import { Spacing, Text, Title } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { VK_UI_DARK_THEME } from 'reactApp/constants';
import {
    type UnlimitedDocumentsTariffType,
    getUnlimitedDocumentsTypeNameGenitive,
    UNLIMITED_DOCUMENTS_RADAR_NAMES,
    UnlimitedDocumentsEntryPoint,
} from 'reactApp/modules/unlimitedDocuments/unlimitedDocuments.const';
import { UnlimitedDocumentsActions } from 'reactApp/modules/unlimitedDocuments/unlimitedDocuments.module';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { UnlimitedDocumentsNote } from 'reactApp/ui/UnlimitedDocuments/UnlimitedDocumentsNote/UnlimitedDocumentsNote';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { isDarkThemeModeEnabled } from 'reactApp/utils/theme';

import styles from './UnlimitedDocumentsAsidePromo.css';
import { getUnlimitedDocumentsAsidePromoCoverImageUrl } from './UnlimitedDocumentsAsidePromo.helpers';

export interface IAsidePromoModalProps {
    hide(): void;
    close(): void;
    complete(): void;

    templateData: { type: UnlimitedDocumentsTariffType };
}

export const UnlimitedDocumentsAsidePromo = memo<IAsidePromoModalProps>(({ templateData, close, complete }) => {
    const { type } = templateData;
    const dispatch = useDispatch();
    const isDarkTheme = isDarkThemeModeEnabled();
    const coverImage = getUnlimitedDocumentsAsidePromoCoverImageUrl(type);

    const sendAnalytics = useCallback((action: string) => {
        sendPaymentGa({
            action,
            name: UNLIMITED_DOCUMENTS_RADAR_NAMES.ASIDE_MODAL,
            eventCategory: ECategoryGa.balloon,
            doc_type: type,
        });
    }, []);

    useEffect(() => {
        sendAnalytics('show');
        emitAnalyticEvent(AnalyticEventNames.UNLIMITED_DOCUMENTS_ASIDE_BANNER_SHOW, { doc_type: type });
    }, [sendAnalytics, type]);

    const handleClick = useCallback(() => {
        complete();
        dispatch(
            UnlimitedDocumentsActions.buyUnlimitedDocumentsTariff({
                type,
                stage: 'thanks',
                offer_source: UnlimitedDocumentsEntryPoint.ASIDE_PROMO,
            })
        );
        sendAnalytics('click');
        emitAnalyticEvent(AnalyticEventNames.UNLIMITED_DOCUMENTS_ASIDE_BANNER_CLICK, { doc_type: type });
    }, [sendAnalytics, type]);

    const handleClose = useCallback(() => {
        close();
        sendAnalytics('close');
        emitAnalyticEvent(AnalyticEventNames.UNLIMITED_DOCUMENTS_ASIDE_BANNER_CLOSE, { doc_type: type });
    }, [sendAnalytics, type]);

    const { title, text } = useMemo(() => {
        const typeNameGenitive = getUnlimitedDocumentsTypeNameGenitive(type);

        return {
            title: (
                <>
                    Получите дополнительное
                    <br />
                    место для {typeNameGenitive}
                </>
            ),
            text: (
                <>
                    Подключите любую подписку Mail Space,
                    <br />
                    чтобы не удалять старые файлы ради новых
                    <br />
                    {typeNameGenitive}
                </>
            ),
        };
    }, [type]);

    return (
        <div
            className={classNames(styles.root, isDarkTheme ? VK_UI_DARK_THEME : styles.light)}
            data-qa-modal={'unlimited-documents-aside-promo'}
        >
            <img className={styles.image} src={coverImage} alt="Cover image" />
            <div className={styles.content}>
                <Title level="1">{title}</Title>
                <Spacing size={16} />
                <Text>{text}</Text>
                <Spacing size={26} />
                <div className={styles.footer}>
                    <Button sizeMode={ButtonSizeMode.extraBig} theme="vk" primary onClick={handleClick}>
                        Подключить за 0 ₽
                    </Button>
                    <Spacing size={8} />
                    <UnlimitedDocumentsNote />
                </div>
            </div>
            <div className={styles.close} onClick={handleClose}>
                <Icon20Cancel width={18} height={18} />
            </div>
        </div>
    );
});

UnlimitedDocumentsAsidePromo.displayName = 'UnlimitedDocumentsAsidePromo';

export default UnlimitedDocumentsAsidePromo;
